import { ChangeDetectionStrategy, Component, computed, inject, input, Signal } from '@angular/core';

import { I18nService } from '@recruitee/i18n';
import { HiddenResultPreview, InterviewResult } from '@recruitee/interview-results-types';
import { Placement } from '@recruitee/placements-types';

import { EvaluationMeterComponent } from './evaluation-meter.component';
import { getPlacementRatingStats, RatingStats } from './rating-stats';

@Component({
  selector: 'rt-placement-evaluation-meter',
  template: `<rt-evaluation-meter
    [rating]="rating()"
    [tooltipText]="tooltipText()"
    [isDisqualified]="isCandidateDisqualified()"
    [isHidden]="isRatingHidden()"
    tooltipEnabled="true"
  />`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [EvaluationMeterComponent],
  host: {
    '(click)': 'dontCloseTooltipOnClick($event)',
  },
})
export class PlacementEvaluationMeterComponent {
  private readonly i18n = inject(I18nService);

  public placement = input.required<Placement>();

  public interviewResults = input.required<Record<number, InterviewResult[]>>();

  public hiddenResultPreview = input.required<HiddenResultPreview | undefined>();

  protected tooltipText = computed(() => {
    if (this.isRatingHidden() && this.hiddenResultPreview()) {
      return this.i18n.translate('app.candidate.placements.eval_meter.hidden_evals_tooltip', {
        count: this.hiddenResultPreview()!.count,
      });
    }

    if (this.rating() === null) {
      return this.i18n.translate('app.candidate.placements.eval_meter.no_evals_tooltip');
    }

    return this.i18n.translate(
      'app.candidate.placements.eval_meter.has_evals_tooltip',
      this.ratingStats(),
    );
  });

  protected rating = computed(() => this.placement().positiveRatings ?? null);

  protected isRatingHidden = computed(
    () => this.placement().ratingVisible !== undefined && !this.placement().ratingVisible,
  );

  protected isCandidateDisqualified = computed(() => !!this.placement().disqualifyReason?.length);

  private ratingStats: Signal<RatingStats> = computed(() =>
    getPlacementRatingStats(this.placement(), this.interviewResults()),
  );

  protected dontCloseTooltipOnClick(event: MouseEvent): void {
    event.stopImmediatePropagation();
  }
}
